import React, { useCallback, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/configureStore";
import OT from "@opentok/client";
import { VenueSessionState, validPublishingStates } from "../../../app/types";
import { useUser } from "../../api/helpers";
import LogError from "../../logging/LogError";
import { getSessionSetting } from "../selectors";
import {
  addMyStream,
  removeMyStream,
  setSessionSetting,
} from "../sessionSlice";
import useSessionId from "../useSessionId";

interface UsePublishScreenShareProps {
  vonageSession: OT.Session;
  addStream: (streamToAdd: OT.Stream) => void;
  removeStream: (streamToRemove: OT.Stream) => void;
  sessionState: VenueSessionState;
}

const usePublishScreenShare = ({
  vonageSession,
  addStream,
  removeStream,
  sessionState,
}: UsePublishScreenShareProps) => {
  const userInfo = useUser();
  const publisher = useRef<OT.Publisher>();
  const dispatch = useAppDispatch();
  const sessionId = useSessionId();
  const isScreenSharing = useAppSelector(
    getSessionSetting(sessionId, "isScreenSharing"),
  );

  const onScreenShareDestroyed = useCallback(() => {
    if (publisher.current?.stream) {
      if (sessionId) {
        dispatch(
          removeMyStream(
            sessionId,
            publisher.current.stream.streamId,
            publisher.current.stream.videoType,
          ),
        );
      }
      removeStream(publisher.current.stream);
    }
    if (publisher.current) {
      publisher.current.destroy();
      publisher.current = undefined;
    }
  }, [dispatch, removeStream, sessionId]);

  // Destroy stream on unmount
  useEffect(
    () => () => onScreenShareDestroyed(),
    // Run on unmount
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [],
  );

  // Destroy stream when user turns it off
  useEffect(() => {
    if (!isScreenSharing && publisher.current?.stream) {
      onScreenShareDestroyed();
    }
  }, [isScreenSharing, onScreenShareDestroyed, vonageSession]);

  // Destroy stream on "View Only"
  useEffect(() => {
    if (!validPublishingStates.includes(sessionState)) {
      onScreenShareDestroyed();
    }
    // Run only on session state changes
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [sessionState]);

  useEffect(() => {
    if (isScreenSharing && publisher.current === undefined) {
      publisher.current = OT.initPublisher(
        undefined,
        {
          insertDefaultUI: false,
          insertMode: "append",
          videoSource: "screen",
          width: "100%",
          height: "100%",
          name: `${userInfo?.firstName} ${userInfo?.lastName}'s screen`,
          publishVideo: true,
          publishAudio: false,
          fitMode: "contain",
        },
        (error) => {
          if (error) {
            dispatch(setSessionSetting(sessionId, "isScreenSharing", false));
            publisher.current = undefined;
            LogError(error);
          } else if (publisher.current) {
            publisher.current.on("streamDestroyed", () => {
              onScreenShareDestroyed();
            });

            publisher.current.on("streamCreated", (event) => {
              if (sessionId) {
                dispatch(
                  addMyStream(
                    sessionId,
                    event.stream.streamId,
                    event.stream.videoType,
                  ),
                );
              }
              addStream(event.stream);
            });

            vonageSession.publish(publisher.current, (publishError) => {
              if (publishError) {
                dispatch(
                  setSessionSetting(sessionId, "isScreenSharing", false),
                );
                LogError(publishError);
                publisher.current = undefined;
              }
            });
          }
        },
      );
    }
    return () => {
      publisher.current?.off("streamDestroyed");
      publisher.current?.off("streamCreated");
    };
  }, [
    addStream,
    removeStream,
    userInfo?.firstName,
    userInfo?.lastName,
    vonageSession,
    onScreenShareDestroyed,
    dispatch,
    sessionId,
    isScreenSharing,
  ]);
};

export default usePublishScreenShare;
